/**
 * Extreme Weather Warnings layer (Phase EWW / L.0 Layer #2 blending).
 *
 * Fetches /api/cap-alerts/extreme for the current bbox (debounced) and renders
 * each active Severe/Extreme CAP alert as a MultiPolygon via a Cesium
 * GeoJsonDataSource, coloured by event keyword (see styles/capHazards). This
 * is the CAP-polygon half of the multi-source "Urgent Anomalies" surface — it
 * mounts ALONGSIDE the AI-fused HighRiskHotspotsLayer and both render together.
 *
 * Visibility is controlled by the blend rule `urgentAnomalies && extremeWeather`
 * (computed in App): turning Layer #2 off hides both; the Weather-group "Extreme
 * Weather Warnings" mute toggle hides only these polygons (hotspots remain).
 *
 * Like ActiveFiresLayer, MapView owns the viewer, so this is an imperative
 * installer: MapView calls `requestRefresh` on camera moves, `setVisible` on the
 * toggle, and `pick` from its central click handler to route a CAP selection to
 * the side panel (ExtremeWeatherBody).
 *
 * Extreme-severity alerts get a thicker STATIC outline (more attention-grabbing
 * than Severe via width, not motion). Per the founder motion-budget revision
 * (2026-06-05, §3B.8) the only ambient map motion is live-camera dots + L3/L4
 * anomaly markers — CAP POLYGONS never breathe. An unmapped event renders grey
 * (styles/capHazards default), never dropped.
 *
 * #359 / #376 — an UNACKED RL-qualifying CAP urgent (server response_level ≥ 3 —
 * Extreme always, plus a life-safety Severe; see {@link isUrgentCapAlert})
 * additionally renders a pulsing URGENT-RED anomaly DOT
 * (the Level-4-equivalent fused-hotspot treatment: ring + breathe + kind-glyph
 * knockout inside), so a tornado warning asserts the same urgency as a fused
 * hotspot. This is a marker, not the polygon — the polygon stays static — and it's
 * the §3B.8-sanctioned "L3/L4 anomaly marker" motion, gated by the lifecycle law
 * (only unacked urgents pulse; an acked alert falls back to the static ACK-STEEL
 * glyph). Everything else stays the #305 ambient static standalone glyph.
 */
import * as Cesium from 'cesium';
import type { CapAlertFeature, CapAlertFeatureCollection } from '@tsr/shared-types';
import { fetchExtremeWeatherWarnings, type BboxQuery } from '../api';
import { extremeWeatherStyle, capEventGlyph } from '../styles/capHazards';
import {
  isAcknowledged,
  lifecyclePolygonStyle,
  lifecycleStyle,
  liftAckedCountryDrop,
  anomalyLevelBillboardColor,
  anomalyGate,
  anomalyScale,
  ACK_STEEL,
} from './anomalyMarker';
import {
  hotspotLevelStyle,
  isResponseLevel,
  RESPONSE_LEVEL_COLORS,
  URGENT_MIN_RESPONSE_LEVEL,
} from '../styles/responseLevel';
import { renderDiscGlyph } from '../discGlyphComposite';
import { isWithinCanadaScope } from '../canadaScope';
import { polygonCentroid } from './polygonCentroid';
import { urgentRedRegistry, type UrgentRedMarker } from '../greenMotion';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

const REFRESH_DEBOUNCE_MS = 500;
const UUID_RE = /^[0-9a-f-]{36}$/i;
/** Registry key for the #255 red-yield reporting (matches the polygon ds name). */
const YIELD_LAYER_KEY = 'extreme-weather-warnings';
/** Static outline widths (px): Extreme reads heavier than Severe via WIDTH, not
 *  motion (§3B.8 founder motion budget). */
const EXTREME_OUTLINE_WIDTH = 4;
const SEVERE_OUTLINE_WIDTH = 2;

/**
 * #359 / #376 — which CAP alert qualifies for the URGENT-RED pulsing anomaly dot
 * (the same treatment a fused high-risk hotspot gets), vs the ambient static glyph.
 *
 * CAP now carries a CANONICAL server-side Response Level (`response_level`, migration
 * 0098) derived from severity + the de-flood-safe life-safety event gate: Extreme→4
 * (always); Severe→3 ONLY for the immediate-life-safety event family (tornado /
 * hurricane / flood / wildfire / evacuation / …), else 2; Moderate→2; Minor→1. We
 * qualify on that ONE field at the urgent floor (RL ≥ URGENT_MIN_RESPONSE_LEVEL = 3),
 * exactly as the queue and the de-flood count do — no more divergent client mapping.
 * The common Severe traffic (squall / thunderstorm → RL2) stays the ambient static
 * glyph, so the §3B.8 motion budget isn't flooded (the #370 de-flood, preserved).
 * Pure + exported for unit tests.
 */
export function isUrgentCapAlert(
  props: { response_level?: number | null } | null | undefined,
): boolean {
  return (props?.response_level ?? 0) >= URGENT_MIN_RESPONSE_LEVEL;
}

/**
 * A representative interior point (≈ centroid) for a CAP MultiPolygon — the anchor
 * the kind glyph rides at (#305). Delegates to the shared {@link polygonCentroid}
 * (extracted icon one-swing #564 so CAP + the two evac-order layers share ONE
 * centroid algorithm). Kept as a thin, exported wrapper so the existing call sites +
 * unit tests are unchanged. Returns null for an empty geometry.
 */
export function capRepresentativePoint(
  geometry: CapAlertFeature['geometry'] | null | undefined,
): [number, number] | null {
  return polygonCentroid(geometry);
}

/** Resolve the CAP uuid carried by a GeoJsonDataSource entity. Cesium copies the
 *  feature `properties` bag onto every sub-polygon entity (whose own entity.id is
 *  a generated GUID), so the property bag is the reliable key; entity.id is the
 *  single-geometry fallback. Pure + exported for unit tests. */
export function readCapId(entity: { id?: unknown; properties?: unknown }): string | null {
  const props = entity.properties as
    | { id?: { getValue?: (time?: unknown) => unknown } }
    | null
    | undefined;
  const idProp = props?.id;
  if (idProp && typeof idProp.getValue === 'function') {
    const v = idProp.getValue();
    if (typeof v === 'string' && v) return v;
  }
  if (typeof entity.id === 'string' && UUID_RE.test(entity.id)) return entity.id;
  return null;
}

export interface ExtremeWeatherWarningsLayerOptions {
  /** Reports the rendered alert count (for the toggle label). */
  onCount?: (n: number) => void;
  /** Current toggle state — used to set ds.show after a fetch completes. */
  getVisible?: () => boolean;
  /** WALL profile (#272, PART XI Rulings 1.3 + 1.6): warnings render
   *  PROMINENT = present at country zoom on the wall — the #153 acked-CAP
   *  recede GATE lifts (acked polygons keep their receded ACK-STEEL
   *  colour/alpha, they just stop dropping from country zoom). Default false
   *  (operator COP — untouched). */
  getLiftAckedGate?: () => boolean;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<CapAlertFeatureCollection>;
}

export interface ExtremeWeatherWarningsLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its CAP feature (null if not ours). */
  pick(entity: unknown): CapAlertFeature | null;
  destroy(): void;
}

export function installExtremeWeatherWarningsLayer(
  viewer: Cesium.Viewer,
  opts: ExtremeWeatherWarningsLayerOptions = {},
): ExtremeWeatherWarningsLayerHandle {
  const fetcher = opts.fetcher ?? fetchExtremeWeatherWarnings;
  const ds = new Cesium.GeoJsonDataSource('extreme-weather-warnings');
  void viewer.dataSources.add(ds);
  // #305: a sibling source for the CAHS kind-glyph point markers. The GeoJson
  // ds.load() REPLACES all entities each refresh, so the glyphs (which we manage
  // by hand) must live in their own CustomDataSource — the polygon law is untouched.
  const glyphDs = new Cesium.CustomDataSource('extreme-weather-glyphs');
  void viewer.dataSources.add(glyphDs);

  const entityToFeature = new Map<Cesium.Entity, CapAlertFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load() resolving after a newer one.
  let loadGen = 0;

  const styleEntities = (fc: CapAlertFeatureCollection): void => {
    const byId = new Map<string, CapAlertFeature>();
    for (const f of fc.features) byId.set(f.id, f);
    entityToFeature.clear();
    for (const entity of ds.entities.values) {
      const id = readCapId(entity);
      const feature = id ? byId.get(id) : undefined;
      // Law 3: an acknowledged CAP alert recedes — recolour to ACK-STEEL, dim its
      // fill/stroke, drop the Extreme thick outline, and drop from country zoom
      // (reads the #140 persisted workstate that already rides feature.properties).
      const acknowledged = isAcknowledged(feature?.properties);
      const style = lifecyclePolygonStyle(
        extremeWeatherStyle(feature?.properties.event),
        feature?.properties,
      );
      const color = Cesium.Color.fromCssColorString(style.color);
      const isExtreme = !acknowledged && feature?.properties.severity === 'Extreme';
      if (entity.polygon) {
        entity.polygon.material = new Cesium.ColorMaterialProperty(color.withAlpha(style.alpha));
        // height 0 (not clamp-to-ground) so the outline renders.
        entity.polygon.height = new Cesium.ConstantProperty(0);
        entity.polygon.outline = new Cesium.ConstantProperty(true);
        entity.polygon.outlineColor = new Cesium.ConstantProperty(color.withAlpha(style.strokeAlpha));
        entity.polygon.outlineWidth = new Cesium.ConstantProperty(
          isExtreme ? EXTREME_OUTLINE_WIDTH : SEVERE_OUTLINE_WIDTH,
        );
        // Acknowledged polygons tier in at regional+ only (anomalyGate semantics for
        // area geometry); fresh ones render at all zoom tiers. PART XI Ruling
        // 1.3/1.6 (wall only): the acked recede GATE lifts — the polygon keeps
        // its receded ACK-STEEL colours but renders at every wall zoom.
        entity.polygon.distanceDisplayCondition =
          acknowledged && !(opts.getLiftAckedGate?.() ?? false)
            ? new Cesium.ConstantProperty(
                new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M),
              )
            : undefined;
      }
      if (feature) entityToFeature.set(entity, feature);
    }
  };

  // The point-marker pass per CAP feature, in glyphDs. Icon one-swing (#564, §C.1):
  // UNIFIED onto the EXACT-ACT DISC — ONE billboard per CAP point, replacing the prior
  // two registers (the #359 urgent pulsing anomaly dot + glow-halo + inside chip, AND
  // the #475 ambient zone-chip standalone glyph). Both were on the retiring chip/glow
  // machinery; both now render as a RESPONSE-LEVEL-filled disc with the CAP event's
  // white kind-glyph knocked out:
  //   - fill = RESPONSE_LEVEL_COLORS[response_level] (Extreme→L4 red, life-safety
  //     Severe→L3 amber, Severe→L2 blue, Minor→L1 slate), or ACK-STEEL when acknowledged;
  //   - the unacked L4 is the single pulsing class (fill-breathe on billboard.color,
  //     §C.1-3); L3/L2/L1 static; an acked alert recedes to the steel disc + regional gate;
  //   - ROBUSTNESS (the Elliot Lake class): when capEventGlyph returns null (an event with
  //     no mapped glyph), draw a GLYPHLESS BARE DISC — a CAP alert still shows a marker,
  //     never a symbol-less gap. (The primary Elliot-Lake fix is the new `evacuation` rule
  //     in capHazards RULES so that event now HAS a glyph; this is the belt-and-braces.)
  // The polygon fill/outline law (styleEntities) is UNTOUCHED — CAP POLYGONS stay ambient
  // (council ruling); only the POINT marker converts. Unacked urgents are reported to the
  // #255 red-yield registry so green camera motion yields to a pulsing CAP urgent.
  const renderGlyphs = (fc: CapAlertFeatureCollection): void => {
    glyphDs.entities.removeAll();
    const urgentMarkers: UrgentRedMarker[] = [];
    for (const feature of fc.features) {
      const point = capRepresentativePoint(feature.geometry);
      if (!point) continue; // geocode-only / empty geometry → polygon-less, no marker
      // Canada-scope render guard (finding 1, 2026-07-06 §5.1): never draw a CAP dot outside
      // Canada (shared-DB ocean test fixtures + continent-zoom fetch bbox). See canadaScope.ts.
      if (!isWithinCanadaScope(point[0], point[1])) continue;
      const position = Cesium.Cartesian3.fromDegrees(point[0], point[1]);
      const glyph = capEventGlyph(feature.properties.event); // null → glyphless bare disc
      const level = feature.properties.response_level;
      // Law 3: acknowledged → ACK-STEEL static, regional-gated; else the level style.
      // PART XI Ruling 1.6 (wall only): the acked country-drop lifts on the wall.
      const base = liftAckedCountryDrop(
        lifecycleStyle(hotspotLevelStyle(level), feature.properties),
        feature.properties,
        opts.getLiftAckedGate?.() ?? false,
      );
      // R4 (PER-FAMILY): L4 is the single pulsing class — drop L3's pulse.
      const style =
        base.pulse && base.level !== 4 ? { ...base, pulse: false, pulseAmplitude: 0 } : base;
      const worked = style.color === ACK_STEEL;
      const fillHex = worked
        ? ACK_STEEL
        : isResponseLevel(level)
          ? RESPONSE_LEVEL_COLORS[level]
          : RESPONSE_LEVEL_COLORS[1];
      const gate = anomalyGate(style);
      const image =
        renderDiscGlyph(glyph ?? 'hot-spot', { fill: fillHex, glyphless: !glyph }) ?? undefined;
      const dot = glyphDs.entities.add({
        id: `capdot:${feature.id}`,
        position,
        billboard: {
          image,
          width: style.baseSize,
          height: style.baseSize,
          color: anomalyLevelBillboardColor(!worked && style.pulse, style.alpha),
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
          scaleByDistance: anomalyScale(style), // #410: declutter the always-on L3/L4 at country zoom
        },
      });
      entityToFeature.set(dot, feature);
      if (!worked && isUrgentCapAlert(feature.properties)) {
        urgentMarkers.push({
          lon: point[0],
          lat: point[1],
          visibleFarM: style.hideAtCountry ? REGIONAL_VISIBLE_FAR_M : Number.POSITIVE_INFINITY,
        });
      }
    }
    // #255 red-yield: report this render's unacked urgents so green camera motion
    // yields while any pulsing CAP urgent is in view (same as the dot anomaly layers).
    urgentRedRegistry.report(YIELD_LAYER_KEY, urgentMarkers);
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      await ds.load(fc, { clampToGround: false });
      if (destroyed || gen !== loadGen) return;
      styleEntities(fc);
      renderGlyphs(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
      glyphDs.show = ds.show;
      urgentRedRegistry.setVisible(YIELD_LAYER_KEY, ds.show);
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchExtremeWeatherWarnings failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
      glyphDs.show = visible;
      // A hidden layer's urgents are not in view: release the #255 red-yield.
      urgentRedRegistry.setVisible(YIELD_LAYER_KEY, visible);
    },
    pick(entity: unknown): CapAlertFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      urgentRedRegistry.clear(YIELD_LAYER_KEY);
      if (!viewer.isDestroyed()) {
        viewer.dataSources.remove(ds, true);
        viewer.dataSources.remove(glyphDs, true);
      }
    },
  };
}
